/**
 * OrderBySection — basic ORDER BY clause rendering. Each comma-separated entry
 * shows the expression + direction (ASC/DESC) + NULLS FIRST/LAST. Per § 1.4
 * each row ships disabled column picker, direction toggle, NULLS toggle
 * (dialect-gated), and `×`; the section ships disabled `[+ add sort]`. ORDER BY
 * has no reorder op, so rows carry no drag grip (a decorative one was removed —
 * it looked draggable and wasn't).
 *
 * VQE-Edit wiring: when `editContext` is supplied AND the resolver service has
 * a schema snapshot, the direction toggle / NULLS toggle / `×` buttons commit
 * SR-Edit ops via `modifyOrderBy` / `removeOrderBy`, and the `[+ add sort]`
 * popover is populated by `useColumnSuggestions` (committing a default-ASC
 * `add-order-by` op). Without `editContext` (or without a snapshot) the
 * disabled VQE-RO scaffolding renders unchanged.
 *
 * NULLS FIRST/LAST is dialect-gated: PostgreSQL / Oracle / SQLite show the
 * toggle; MSSQL / MySQL hide it. Caller's responsibility to pre-gate by
 * dialect (we read `options.engine`).
 */

import { useMemo } from 'react';

import type { ParseOptions, SqlEngine } from '../../../../sql-grammar/model';
import type { VqeEditContext } from '../../edit/editContext';
import { extractStructureForContext } from '../../edit/editContext';
import { inScopeRelationPrefixes, shouldFlagOutOfScope } from '../common/inScopeRelations';
import { ClauseCard, EMPTY_CLAUSE_LABEL } from './ClauseCard';
import type { SelectOutline } from './extractSelectOutline';
import { OrderByAddPopover } from './OrderByAddPopover';
import { OrderByItemRow } from './OrderByItemRow';
import { parseOrderItem } from './parseSelectItem';
import { splitTopLevelCommas } from './splitTopLevel';

export interface OrderBySectionProps {
  readonly bufferText: string;
  readonly outline: SelectOutline;
  readonly options: ParseOptions;
  readonly editContext?: VqeEditContext;
  /** Render nothing for an empty clause — the SELECT's clause add row offers it. */
  readonly hideEmpty?: boolean;
}

const NULLS_SUPPORTED: ReadonlySet<SqlEngine> = new Set<SqlEngine>(['postgres', 'oracle', 'sqlite']);

export function OrderBySection({ bufferText, outline, options, editContext, hideEmpty }: OrderBySectionProps) {
  const showNullsToggle = NULLS_SUPPORTED.has(options.engine);
  const editReady = !!editContext && !!editContext.service.getSchemaSnapshot();

  // Folded aliases + table names in the query's FROM/JOIN — drives the
  // out-of-scope red marking on sort entries referencing an absent table.
  const inScopePrefixes = useMemo(() => {
    if (!editContext || !editContext.service.getSchemaSnapshot()) return new Set<string>();
    try {
      return inScopeRelationPrefixes(extractStructureForContext(editContext));
    } catch {
      return new Set<string>();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editContext, bufferText]);

  const items = outline.orderBy
    ? splitTopLevelCommas(bufferText, outline.orderBy.start, outline.orderBy.end, options).map((s) =>
        parseOrderItem(bufferText, s.start, s.end, options),
      )
    : [];

  // When the section is missing entirely AND we can edit, we still render the
  // [+ add sort] affordance so the user can synthesize ORDER BY from scratch.
  if (!outline.orderBy && !editContext) return null;

  // Empty + editable → a one-line keyword row (`ORDER BY — none · [+ Sort by
  // column]`) instead of a full card. The RO empty diagnostic card is preserved
  // below for the no-edit path.
  if (items.length === 0 && editContext) {
    if (hideEmpty) return null;
    return (
      <ClauseCard
        title="Order By"
        testId="vqe-order-by-row"
        density="compact"
        footer={<OrderByAddPopover editContext={editContext} itemCount={0} />}
      >
        <span className="text-xs italic text-muted-foreground">{EMPTY_CLAUSE_LABEL}</span>
      </ClauseCard>
    );
  }

  return (
    <ClauseCard
      title="Order By"
      testId="vqe-order-by-section"
      density="line"
      footer={<OrderByAddPopover editContext={editContext} itemCount={items.length} />}
    >
      {items.length === 0 ? (
        <p className="text-xs italic text-muted-foreground" data-testid="vqe-order-by-empty">
          No sorts parsed.
        </p>
      ) : (
        // Sort columns sit side by side and wrap, like the other clauses' chips.
        <ul className="flex flex-wrap items-center gap-1.5" data-testid="vqe-order-by-items">
          {items.map((item, idx) => (
            <OrderByItemRow
              key={`${item.expression}-${idx}`}
              item={item}
              index={idx}
              showNullsToggle={showNullsToggle}
              editContext={editContext}
              editReady={editReady}
              statementStartOffset={outline.statementStart}
              outOfScope={shouldFlagOutOfScope(item.expression, inScopePrefixes)}
            />
          ))}
        </ul>
      )}
    </ClauseCard>
  );
}
